import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import { ShieldCheck, Layers, FileText, Mail, ArrowRight, Globe } from "lucide-react";
import LanguagePicker from "@/components/LanguagePicker";
import TryLiveDemo from "@/components/TryLiveDemo";

export default function Landing() {
    const { t, lang, setLang } = useI18n();

    return (
        <div className="min-h-screen bg-white">
            {/* Nav */}
            <header className="border-b border-gray-200 bg-white/80 backdrop-blur sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
                        <div className="h-8 w-8 rounded-md bg-[#0b905d] flex items-center justify-center">
                            <span className="text-white font-display font-bold">W</span>
                        </div>
                        <span className="font-display font-bold text-lg tracking-tight text-[#00153f]">
                            widerruf<span className="text-[#0b905d] text-sm font-semibold">.online</span>
                        </span>
                    </Link>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Link to="/pricing" className="text-sm text-gray-600 hover:text-[#00153f] px-3 py-1" data-testid="nav-pricing">{t("nav_pricing")}</Link>
                        <Link to="/demo" className="text-sm text-gray-600 hover:text-[#00153f] px-3 py-1" data-testid="nav-demo">{t("nav_demo")}</Link>
                        <LanguagePicker testid="lang-picker-landing" />
                        <Link to="/login" data-testid="nav-login">
                            <Button variant="ghost" className="text-sm text-[#00153f] hover:bg-[#00153f] hover:text-white">{t("nav_login")}</Button>
                        </Link>
                        <Link to="/register" data-testid="nav-register">
                            <Button className="bg-[#0b905d] hover:bg-[#067a4d] text-white text-sm">{t("nav_register")}</Button>
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="bg-grain">
                <div className="max-w-7xl mx-auto px-6 pt-24 pb-20 grid lg:grid-cols-12 gap-12 items-center">
                    <motion.div
                        className="lg:col-span-7"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-mono uppercase tracking-widest mb-6">
                            <ShieldCheck className="w-3.5 h-3.5" />
                            {t("hero_eyebrow")}
                        </div>
                        <h1 className="font-display text-4xl/[1.25] sm:text-5xl/[1.25] lg:text-6xl/[1.25] font-bold tracking-tight text-[#00153f]">
                            {t("hero_title")}
                        </h1>
                        <p className="mt-6 text-lg text-gray-600 max-w-2xl leading-relaxed">
                            {t("hero_sub")}
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/register" data-testid="cta-start">
                                <Button className="bg-[#0b905d] hover:bg-[#067a4d] text-white h-11 px-6">
                                    {t("cta_start")} <ArrowRight className="ml-2 w-4 h-4" />
                                </Button>
                            </Link>
                            <a href="#try-live" data-testid="cta-try-live">
                                <Button variant="outline" className="h-11 px-6 border-slate-300 text-[#00153f] hover:bg-[#00153f] hover:text-white hover:border-[#00153f]">{t("try_live_cta")}</Button>
                            </a>
                        </div>
                        <div className="mt-10 flex items-center gap-6 text-xs font-mono uppercase tracking-widest text-gray-500">
                            <span>EU 2023/2673</span>
                            <span className="h-3 w-px bg-gray-300"></span>
                            <span>{t("trust_multishop")}</span>
                            <span className="h-3 w-px bg-gray-300"></span>
                            <span>{t("trust_dsgvo")}</span>
                        </div>
                    </motion.div>

                    <motion.div
                        className="lg:col-span-5"
                        initial={{ opacity: 0, scale: 0.97 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                    >
                        <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
                            <div className="border-b border-gray-200 px-4 py-3 bg-slate-50 flex items-center justify-between">
                                <span className="text-xs font-mono uppercase tracking-widest text-gray-500">{t("dashboard_preview")}</span>
                                <div className="flex gap-1">
                                    <span className="w-2 h-2 rounded-full bg-gray-300" />
                                    <span className="w-2 h-2 rounded-full bg-gray-300" />
                                    <span className="w-2 h-2 rounded-full bg-[#00153f]" />
                                </div>
                            </div>
                            <div className="p-5 space-y-4">
                                <div className="grid grid-cols-3 gap-3">
                                    {[
                                        { label: t("status_new"), val: "12" },
                                        { label: t("status_in_progress"), val: "5" },
                                        { label: t("status_accepted"), val: "84" },
                                    ].map((s, i) => (
                                        <div key={i} className="border border-gray-200 rounded-md p-3">
                                            <div className="text-xs text-gray-500 font-mono uppercase tracking-wider">{s.label}</div>
                                            <div className="font-display text-2xl font-bold text-[#00153f] mt-1">{s.val}</div>
                                        </div>
                                    ))}
                                </div>
                                <div className="border border-gray-200 rounded-md">
                                    {[
                                        { ref: "A8F12C", name: "Maria Becker", st: "status-new", stl: t("status_new") },
                                        { ref: "B71D90", name: "Jonas Schmidt", st: "status-in_progress", stl: t("status_in_progress") },
                                        { ref: "C32E45", name: "Elif Yıldız", st: "status-accepted", stl: t("status_accepted") },
                                    ].map((r, i) => (
                                        <div key={i} className={`flex items-center justify-between px-3 py-2.5 ${i !== 2 ? 'border-b border-gray-100' : ''}`}>
                                            <div className="flex items-center gap-3">
                                                <span className="font-mono text-xs text-gray-500">{r.ref}</span>
                                                <span className="text-sm text-[#00153f]">{r.name}</span>
                                            </div>
                                            <span className={`status-pill ${r.st}`}><span className="dot" />{r.stl}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Try it Live Demo Widget */}
            <TryLiveDemo />

            {/* Features */}
            <section className="py-20 border-t border-gray-200">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { icon: Layers, t: t("feat_1_t"), d: t("feat_1_d") },
                            { icon: ShieldCheck, t: t("feat_2_t"), d: t("feat_2_d") },
                            { icon: Mail, t: t("feat_3_t"), d: t("feat_3_d") },
                            { icon: FileText, t: t("feat_4_t"), d: t("feat_4_d") },
                        ].map((f, i) => {
                            const Icon = f.icon;
                            return (
                                <div key={i} className="border border-gray-200 rounded-md p-6 bg-white card-lift">
                                    <div className="h-10 w-10 rounded-md bg-[#00153f] flex items-center justify-center mb-4">
                                        <Icon className="text-white w-5 h-5" />
                                    </div>
                                    <h3 className="font-display font-semibold text-[#00153f] text-lg">{f.t}</h3>
                                    <p className="mt-2 text-sm text-gray-600 leading-relaxed">{f.d}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <footer className="border-t border-gray-200 py-8">
                <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <span className="text-xs font-mono uppercase tracking-widest text-gray-500">© 2026 {t("app_name")}</span>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 text-xs text-gray-500">
                        <div className="flex items-center gap-6">
                            <Link to="/impressum" className="hover:text-[#00153f]" data-testid="footer-impressum">{t("footer_impressum")}</Link>
                            <Link to="/datenschutz" className="hover:text-[#00153f]" data-testid="footer-datenschutz">{t("footer_datenschutz")}</Link>
                            <button
                                type="button"
                                onClick={() => window.dispatchEvent(new Event("widerruf:open-cookie-settings"))}
                                className="hover:text-[#00153f]"
                                data-testid="footer-cookie-settings"
                            >
                                {t("cookie_settings")}
                            </button>
                        </div>
                        <span>{t("app_tagline")}</span>
                    </div>
                </div>
            </footer>
        </div>
    );
}
